import { type LocaleCode, type SupplierListItem } from '@choco-admin/types';

export type FormMode = 'create' | 'edit';

export enum TabType {
  PROFILE = 'PROFILE',
  ORDER_PREFERENCES = 'ORDER_PREFERENCES',
  CATALOG = 'CATALOG',
  DELIVERY_ZONES = 'DELIVERY_ZONES',
  TEAM = 'TEAM',
  SETTINGS = 'SETTINGS',
  NOTIFICATIONS = 'NOTIFICATIONS',
  PROMOTIONS = 'PROMOTIONS',
  TAX = 'TAX',
  ORDER_AGENT = 'ORDER_AGENT',
  FEATURE_FLAGS = 'FEATURE_FLAGS',
}

export const tabLabels: Record<TabType, string> = {
  PROFILE: 'Profile',
  ORDER_PREFERENCES: 'Order Preferences',
  CATALOG: 'Catalog',
  DELIVERY_ZONES: 'Delivery Zones',
  TEAM: 'Team',
  SETTINGS: 'Settings',
  NOTIFICATIONS: 'Notifications',
  PROMOTIONS: 'Promotions',
  TAX: 'Tax',
  ORDER_AGENT: 'Order Agent',
  FEATURE_FLAGS: 'Feature Flags',
};

export interface TabProps {
  mode: FormMode;
  supplier?: SupplierListItem;
}

export interface SupplierFormData {
  // Profile
  name: string;
  /**
   * The vendor's own code for this distributor. Empty string means "not set";
   * the backend rejects `''`, so a save turns it into `null` (see
   * `buildSupplierUpdateInput`).
   */
  code: string;
  /**
   * The vendor's own name for this distributor. Empty string means "not set",
   * and a save sends it as is: the backend clears the name on `''`.
   */
  externalName: string;
  description?: string;
  primaryCategory: string;
  secondaryCategories?: string[];
  deliveryFee?: string;
  minimumOrderValue?: string;
  minimumOrderCurrency?: string;
  minOrderStrict: boolean;

  // Order preferences
  allowCustomProducts: boolean;
  mandatoryCustomerNumber: boolean;
  mandatoryDeliveryDate: boolean;
  enableProductComments: boolean;
  allowFractionalUnits: boolean;
  allowOnlyOneVariant: boolean;
  sortBy: 'alphabetical' | 'orderGuide';

  // Catalog
  supplierDefaultSubCatalog: string;
  displayInMarketplace: boolean;
  displayToCustomers: boolean;
  supplierHideMyPrice: boolean;
  showOutOfStockProducts: boolean;

  // Ordering and eCommerce (POR-31)
  allowInvoiceOrderGuides: boolean;
  disableCustomerNumberEdits: boolean;
  restrictSalesRepDeliverySettingEdits: boolean;
  /**
   * Named for the control, which reads "Disable ordered product merging". It maps
   * straight onto `allowOrderedProductMerging` with no inversion, because that
   * stored field already carries the "disabled" meaning. See the note on
   * `Supplier.allowOrderedProductMerging`.
   */
  disableOrderedProductMerging: boolean;
  enableEcommerceProductGrid: boolean;
  allowEcommercePoNumber: boolean;
  allowCustomerAcquisition: boolean;

  // Delivery settings
  cutoffType: 'strict' | 'flexible';
  // deliveryDays and cutoffTimes store the per-day boolean/time maps that
  // DeliverySettingsSection registers, as does cutoffDays, via setValue.
  deliveryDays: Record<string, boolean>;
  cutoffTimes: Record<string, string>;
  cutoffDays?: Record<string, string>;

  // Settings (fields supported by SupplierListItem / save boundary)
  isIntegrated: boolean;
  enablePromotions: boolean;
  enableCrossUnitPromotions: boolean;
  enableTaxes: boolean;
  pricingDateSource: 'ORDER_DATE' | 'DELIVERY_DATE';
  /** Undefined means the distributor has none set and the currency default applies. */
  calculationPrecision?: number;
  referenceId: string;
  locale: LocaleCode;
  /**
   * IANA zone name, e.g. 'Europe/Berlin'. Empty string means "not set", which
   * the backend reads as America/Los_Angeles when it computes delivery dates.
   */
  timezone: string;

  // Notification email addresses
  notificationOrderEmails: string;
  notificationSystemAlertEmails: string;
  notificationComplaintEmails: string;
  notificationWhatsappNumber: string;
  notificationSmsNumber: string;
  /** Attach a PDF copy of the order to the distributor's order emails. */
  notificationOrderPdf: boolean;
  /** Attach an Excel copy of the order to the distributor's order emails. */
  notificationOrderXlsx: boolean;
}
